import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { loginUser } from '../../services/api';
import { setAuthToken, isAuthenticated, setUser } from '../../utils/auth';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';


const CAPTCHA_ENABLED = process.env.REACT_APP_CAPTCHA_ENABLED === 'true';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const { executeRecaptcha } = useGoogleReCaptcha();
  const navigate = useNavigate();
  const [captchaReady, setCaptchaReady] = useState(false);

  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/dashboard');
    }
  }, [navigate]);

  useEffect(() => {
    // Check if reCAPTCHA is ready
    if (executeRecaptcha) {
      setCaptchaReady(true);
    }
  }, [executeRecaptcha]);

  const handleReCaptchaVerify = async () => {

    if (!CAPTCHA_ENABLED) {
      return 'disabled';
    }
    
    if (!executeRecaptcha || !captchaReady) {
      console.warn('reCAPTCHA not yet available or initialized');
      setError('reCAPTCHA is initializing. Please try again in a moment.');
      throw new Error('reCAPTCHA not ready');
    }
    
    // if (!executeRecaptcha) {
    //   console.warn('Execute recaptcha not yet available');
    //   return 'disabled';
    // }
    
    // if (!CAPTCHA_ENABLED) {
    //   return 'disabled';
    // }

    try {
      const token = await executeRecaptcha('login');
      return token;
    } catch (error) {
      console.error('reCAPTCHA error:', error);
      throw new Error('Failed to verify reCAPTCHA');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    if (CAPTCHA_ENABLED && !executeRecaptcha) {
      // Force reload the page to reinitialize reCAPTCHA
      window.location.reload();
      return;
    }
    
    try {
      const captchaToken = await handleReCaptchaVerify();
      const data = await loginUser(email, password, captchaToken);
      setAuthToken(data.accessToken);
      setUser(email);
      navigate('/dashboard');
    } catch (error) {
      setError(error.message || 'Failed to login. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="flex justify-center items-center min-h-screen p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Login</CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium">
                Email address
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium">
                Password
              </label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>


            <div className="text-sm">
              <a href="/forgot-password" className="text-primary hover:underline">
                Forgot your password?
              </a>
            </div>

            <Button type="submit" className="w-full" disabled={isLoading 
              || (CAPTCHA_ENABLED && !captchaReady)
              }>
              {isLoading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="text-center">
          Don't have an account?{' '}
          <a href="/register" className="text-primary hover:underline">
            Register
          </a>
        </CardFooter>
      </Card>
    </div>    
  );
};

export default Login;